import { CommentData, ProductRowFields } from 'types'

type MaybeNum = number | string | null | undefined

const getStartedAt = (r: any): number => {
  const v = r?.startDate
  if (!v) return Number.POSITIVE_INFINITY
  if (v instanceof Date) return v.getTime()
  if (typeof v === 'number') return v
  const t = Date.parse(v) // handles ISO strings
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

// 2) Sort by oldest first (smallest timestamp first) so “most recent goes to the bottom of the 5”
export const sortByAge = (itemsArray: object[]) => {
  return [...itemsArray].sort((a, b) => {
    const ta = getStartedAt(a)
    const tb = getStartedAt(b)
    if (ta !== tb) return ta - tb // earlier (older) first
    // optional tiebreaker: stable by rowId
    return String(a).localeCompare(String(b))
  })
}

const getTimeCreated = (r: any): number => {
  const v = r?.createdAt || r?.creation_timestamp || r?.timeCreated
  if (!v) return Number.POSITIVE_INFINITY
  if (v instanceof Date) return v.getTime()
  if (typeof v === 'number') return v
  const t = Date.parse(v) // handles ISO strings
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

export const sortByTimeCreated = (itemsArray: object[]) => {
  return [...itemsArray].sort((a, b) => {
    const ta = getTimeCreated(a)
    const tb = getTimeCreated(b)
    if (ta !== tb) return tb - ta // earlier (older) first
    // optional tiebreaker: stable by rowId
    return String(a).localeCompare(String(b))
  })
}
export const sortCommentsByTimeCreated = (itemsArray: CommentData[]) => {
  return [...itemsArray].sort((a, b) => {
    const ta = getTimeCreated(a)
    const tb = getTimeCreated(b)
    if (ta !== tb) return tb - ta // earlier (older) first
    // optional tiebreaker: stable by rowId
    return String(a).localeCompare(String(b))
  })
}
const toNumber = (v: MaybeNum): number => {
  if (v == null) return 0
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const monthsBetweenInclusive = (start?: string | Date, end?: string | Date): number => {
  if (!start || !end) return 0
  const s = new Date(start)
  const e = new Date(end)
  if (isNaN(+s) || isNaN(+e) || e < s) return 0
  const years = e.getFullYear() - s.getFullYear()
  const months = e.getMonth() - s.getMonth()
  return years * 12 + months + 1 // Jan–Jan = 1
}

/**
 * Best-effort numeric net total for a product.
 * Priority:
 *   1) netTotal (preferred for your data)
 *   2) total
 *   3) (billedPrice || price) * qty
 *   4) (billedPrice || price) * monthsBetween(startDate, endDate)
 */
export const getProductNetTotal = (p: ProductRowFields): number => {
  const net = toNumber(p.netTotal)
  if (net > 0) return net

  const tot = toNumber(p.total)
  if (tot > 0) return tot

  const unit = toNumber(p.billedPrice ?? p.price)
  if (unit > 0) {
    const q = toNumber(p.qty)
    if (q > 0) return unit * q

    const months = monthsBetweenInclusive(p.startDate, p.endDate)
    if (months > 0) return unit * months

    // treat as one-time if no qty/months
    return unit
  }

  return 0
}

/** Sort products by net total (desc by default). Non-mutating. */
export const sortProductsByNetTotal = (
  products: ProductRowFields[],
  direction: 'asc' | 'desc' = 'desc'
): ProductRowFields[] => {
  const dir = direction === 'asc' ? 1 : -1
  return [...products].sort((a, b) => (getProductNetTotal(a) - getProductNetTotal(b)) * dir)
}

/** Sum an org’s products to a single contract total (net). */
export const sumOrgProductsNetTotal = (products: ProductRowFields[]): number =>
  products.reduce((acc, p) => acc + getProductNetTotal(p), 0)
